import { Chip } from '@heroui/react';
import type { ProjectCostResult } from '@/api/calculations';

interface Props {
  cost: ProjectCostResult;
}

export default function ProjectCostSummary({ cost }: Props) {
  const fmt = (n: number) => `$${n.toFixed(2)}`;

  return (
    <div className="rounded-lg border p-4 mt-4" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-panel)' }}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
        Project Cost Summary
      </h3>

      {/* Counts */}
      <div className="flex gap-6 mb-4 text-xs" style={{ color: 'var(--text-muted)' }}>
        <span>{cost.total_cabinets} cabinets</span>
        <span>{cost.total_doors} doors</span>
        <span>{cost.total_drawers} drawers</span>
        <span>{cost.total_shelves} shelves</span>
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
        {/* Cost breakdown */}
        <div style={{ color: 'var(--text-secondary)' }}>Framing Materials</div>
        <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>
          {cost.material_cost_source === 'pending' ? (
            <Chip size="sm" color="warning" variant="flat" classNames={{ base: 'h-5 px-1', content: 'text-[10px] font-semibold' }}>PENDING</Chip>
          ) : fmt(cost.total_framing_material_cost)}
        </div>

        <div style={{ color: 'var(--text-secondary)' }}>Finish Materials</div>
        <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>
          {cost.material_cost_source === 'pending' ? (
            <Chip size="sm" color="warning" variant="flat" classNames={{ base: 'h-5 px-1', content: 'text-[10px] font-semibold' }}>PENDING</Chip>
          ) : fmt(cost.total_finish_material_cost)}
        </div>

        <div style={{ color: 'var(--text-secondary)' }}>Hardware</div>
        <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(cost.total_hardware_cost)}</div>

        <div style={{ color: 'var(--text-secondary)' }}>Finish Application</div>
        <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(cost.total_finish_cost)}</div>

        <div style={{ color: 'var(--text-secondary)' }}>Labor</div>
        <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(cost.total_labor_cost)}</div>

        <div style={{ color: 'var(--text-secondary)' }}>Custom Charges</div>
        <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(cost.total_custom_charges)}</div>

        <div style={{ color: 'var(--text-secondary)' }}>Toe Kicks</div>
        <div className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(cost.total_toe_kick_cost)}</div>

        <div className="border-t pt-1 mt-1 font-medium" style={{ color: 'var(--text-primary)', borderColor: 'var(--border)' }}>Subtotal</div>
        <div className="border-t pt-1 mt-1 text-right font-mono font-medium" style={{ color: 'var(--text-primary)', borderColor: 'var(--border)' }}>{fmt(cost.subtotal_cost)}</div>

        {/* Margins */}
        {cost.total_margin > 0 && (
          <>
            <div className="mt-2 text-[11px] font-medium" style={{ color: 'var(--text-muted)' }}>
              Margin ({cost.margin_profile_name})
            </div>
            <div className="mt-2" />

            {cost.framing_material_margin_amount > 0 && (<>
              <div className="pl-2" style={{ color: 'var(--text-muted)' }}>Framing material margin</div>
              <div className="text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(cost.framing_material_margin_amount)}</div>
            </>)}
            {cost.finish_material_margin_amount > 0 && (<>
              <div className="pl-2" style={{ color: 'var(--text-muted)' }}>Finish material margin</div>
              <div className="text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(cost.finish_material_margin_amount)}</div>
            </>)}
            {cost.hardware_margin_amount > 0 && (<>
              <div className="pl-2" style={{ color: 'var(--text-muted)' }}>Hardware margin</div>
              <div className="text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(cost.hardware_margin_amount)}</div>
            </>)}
            {cost.labor_margin_amount > 0 && (<>
              <div className="pl-2" style={{ color: 'var(--text-muted)' }}>Labor margin</div>
              <div className="text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(cost.labor_margin_amount)}</div>
            </>)}
            {cost.finish_margin_amount > 0 && (<>
              <div className="pl-2" style={{ color: 'var(--text-muted)' }}>Finish margin</div>
              <div className="text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(cost.finish_margin_amount)}</div>
            </>)}
            {cost.overall_margin_amount > 0 && (<>
              <div className="pl-2" style={{ color: 'var(--text-muted)' }}>Overall margin</div>
              <div className="text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(cost.overall_margin_amount)}</div>
            </>)}

            <div style={{ color: 'var(--text-secondary)' }}>Total Margin</div>
            <div className="text-right font-mono" style={{ color: 'var(--text-secondary)' }}>{fmt(cost.total_margin)}</div>
          </>
        )}

        {/* Grand total */}
        <div className="border-t pt-2 mt-2 text-sm font-bold" style={{ color: 'var(--text-primary)', borderColor: 'var(--border)' }}>
          Grand Total
        </div>
        <div className="border-t pt-2 mt-2 text-right font-mono text-sm font-bold" style={{ color: 'var(--accent)', borderColor: 'var(--border)' }}>
          {fmt(cost.grand_total)}
        </div>
      </div>
    </div>
  );
}
